import type { PlaidInstitution } from '@/api/generated/model';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ADD_ACCOUNT_CATEGORIES,
  POPULAR_INSTITUTIONS,
  filterAddAccountCategories,
  filterPopularInstitutions,
  getAddAccountCategory,
  supportsAutomatedConnect,
  type AddAccountCategory,
  type AddAccountCategoryId,
} from '@/features/accounts/addAccountCategories';
import { usePlaidInstitutions } from '@/features/plaid/usePlaidInstitutions';
import { useLanguage } from '@/i18n/language-hooks';
import { cn } from '@/lib/utils';
import { Building2, PencilLine } from 'lucide-react';
import { useMemo, useState, type KeyboardEvent } from 'react';

export type AddAccountSelection =
  | { kind: 'manual'; category: AddAccountCategory }
  | { kind: 'automated'; category: AddAccountCategory; institution?: PlaidInstitution };

const TONE_TILE: Record<AddAccountCategory['tone'], string> = {
  green: 'bg-fin-green/15 text-fin-green',
  accent: 'bg-fin-accent/15 text-fin-accent',
  cyan: 'bg-fin-cyan/15 text-fin-cyan',
  blue: 'bg-fin-blue/15 text-fin-blue',
  purple: 'bg-fin-purple/15 text-fin-purple',
  info: 'bg-fin-info/15 text-fin-info',
};

/**
 * Command-palette entry point for adding an account: pick what kind of holding
 * it is, then either connect the institution automatically or type the balance
 * in by hand. The automated half only appears where a provider can actually
 * cover the category (`plaidProducts`) and the institution search is reachable.
 */
export function AddAccountPalette({
  onSelect,
  isConnecting = false,
  variant = 'page',
  initialCategoryId,
  categories = ADD_ACCOUNT_CATEGORIES,
}: {
  onSelect: (selection: AddAccountSelection) => void;
  isConnecting?: boolean;
  /** `aside` drops the outer card chrome so the palette sits flush in DetailAside. */
  variant?: 'page' | 'aside';
  /** Open already drilled into this category (e.g. investment on trade). */
  initialCategoryId?: AddAccountCategoryId;
  /** Category catalog for home / search / manual fallback. Defaults to the full banking set. */
  categories?: AddAccountCategory[];
}) {
  const { t } = useLanguage();
  const [categoryId, setCategoryId] = useState<AddAccountCategoryId | null>(
    initialCategoryId ?? null
  );
  const [query, setQuery] = useState('');
  const isAside = variant === 'aside';

  const category = categoryId ? getAddAccountCategory(categoryId) : null;
  const { institutions, isSearching, canSearch, isUnavailable } = usePlaidInstitutions(
    query,
    category?.plaidProducts ?? []
  );
  /** Provider search is down or unconfigured — offer manual entry only. */
  const showAutomated = canSearch && !isUnavailable;

  const popularInstitutions = useMemo(
    () => filterPopularInstitutions(POPULAR_INSTITUTIONS, categories),
    [categories]
  );

  const matchedCategories = useMemo(
    () =>
      filterAddAccountCategories(categories, query, (entry) => [
        t(`accounts.add.categories.${entry.id}.title`),
        t(`accounts.add.categories.${entry.id}.subtitle`),
        ...entry.types.map((type) => t(`accounts.types.${type}`)),
      ]),
    [categories, query, t]
  );

  const openCategory = (next: AddAccountCategory) => {
    setCategoryId(next.id);
    setQuery('');
  };

  const goHome = () => {
    setCategoryId(null);
    setQuery('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const goesBack =
      event.key === 'Escape' || (event.key === 'Backspace' && query.length === 0 && categoryId);
    if (goesBack && categoryId) {
      event.preventDefault();
      goHome();
    }
  };

  // Fallback list: everything can still be entered by hand, so surface the
  // manual options whenever the search turns up nothing to connect.
  const showManualFallback = category
    ? true
    : matchedCategories.length === 0 || query.trim().length > 0;
  const noSearchResults = category
    ? showAutomated && !isSearching && query.trim().length > 0 && institutions.length === 0
    : query.trim().length > 0 && matchedCategories.length === 0;

  return (
    <Command
      shouldFilter={false}
      className={cn(
        'overflow-hidden',
        isAside ? 'bg-transparent' : 'bg-fin-gray-900/40 border-fin-gray-800 rounded-2xl border'
      )}
    >
      <nav
        aria-label={t('accounts.add.palette.breadcrumb')}
        className={cn('flex items-center gap-2', isAside ? 'px-0 pt-0' : 'px-4 pt-4')}
      >
        <BreadcrumbChip label={t('accounts.add.palette.home')} onClick={goHome} />
        {category ? (
          <>
            <span className='text-fin-gray-500' aria-hidden>
              ›
            </span>
            <BreadcrumbChip label={t(`accounts.add.categories.${category.id}.title`)} />
          </>
        ) : null}
      </nav>
      {/* cmdk's own input: it owns arrow/Enter handling and aria-activedescendant. */}
      <div className='relative'>
        <CommandInput
          autoFocus
          value={query}
          onValueChange={setQuery}
          onKeyDown={handleKeyDown}
          placeholder={
            category
              ? t('accounts.add.palette.searchInstitution')
              : t('accounts.add.palette.searchHome')
          }
          className={cn(
            'text-fin-white placeholder:text-fin-gray-500',
            isAside ? 'h-11 text-base' : 'h-14 text-lg'
          )}
        />
        {categoryId ? (
          <kbd
            className={cn(
              'bg-fin-gray-800 text-fin-gray-500 absolute top-1/2 -translate-y-1/2 rounded px-2 py-1 text-xs',
              isAside ? 'right-0' : 'right-4'
            )}
          >
            ESC
          </kbd>
        ) : null}
      </div>

      <CommandList
        className={cn(isAside ? 'max-h-[min(32rem,calc(100vh-12rem))] p-0' : 'max-h-[26rem] p-2')}
      >
        {!category && !query && popularInstitutions.length > 0 ? (
          <div className={cn('pb-1', isAside ? 'pt-3' : 'px-2 pt-2')}>
            <p className='text-fin-gray-500 mb-2 text-xs font-medium'>
              {t('accounts.add.palette.popular')}
            </p>
            <div className='flex flex-wrap gap-2'>
              {popularInstitutions.map((institution) => (
                <button
                  key={institution.name}
                  type='button'
                  onClick={() => {
                    setCategoryId(institution.categoryId);
                    setQuery(institution.name);
                  }}
                  className='border-fin-gray-800 text-fin-white hover:bg-fin-gray-800/60 flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm'
                >
                  <span className='bg-fin-accent/15 text-fin-accent flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold'>
                    {institution.name.charAt(0)}
                  </span>
                  {institution.name}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {noSearchResults ? (
          <div className='bg-fin-gray-800/40 text-fin-gray-500 m-2 rounded-lg p-4 text-sm'>
            {t('accounts.add.palette.noResults', { query: query.trim() })}
          </div>
        ) : null}

        {!category && matchedCategories.length > 0 ? (
          <CommandGroup heading={query ? t('accounts.add.palette.categories') : undefined}>
            {matchedCategories.map((entry) => (
              <PaletteRow
                key={entry.id}
                compact={isAside}
                title={t(`accounts.add.categories.${entry.id}.title`)}
                subtitle={t(`accounts.add.categories.${entry.id}.subtitle`)}
                icon={
                  <span
                    className={cn(
                      'flex items-center justify-center rounded-xl',
                      isAside ? 'h-9 w-9' : 'h-10 w-10',
                      TONE_TILE[entry.tone]
                    )}
                  >
                    <entry.icon className={isAside ? 'h-4 w-4' : 'h-5 w-5'} aria-hidden />
                  </span>
                }
                onSelect={() =>
                  supportsAutomatedConnect(entry, true)
                    ? openCategory(entry)
                    : onSelect({ kind: 'manual', category: entry })
                }
              />
            ))}
          </CommandGroup>
        ) : null}

        {category && showAutomated ? (
          <CommandGroup heading={t('accounts.add.palette.connectAutomatically')}>
            {isSearching ? <Skeleton className='m-2 h-12' /> : null}
            {institutions.map((institution) => (
              <PaletteRow
                key={institution.institutionId}
                compact={isAside}
                title={institution.name}
                subtitle={[institution.url, t('accounts.add.palette.viaPlaid')]
                  .filter(Boolean)
                  .join(' · ')}
                icon={<InstitutionAvatar institution={institution} compact={isAside} />}
                trailing={
                  institution.healthScore != null ? (
                    <Badge variant='outline'>
                      {t('accounts.add.palette.healthScore', {
                        score: Math.round(institution.healthScore * 100),
                      })}
                    </Badge>
                  ) : undefined
                }
                disabled={isConnecting}
                onSelect={() => onSelect({ kind: 'automated', category, institution })}
              />
            ))}
            {!isSearching && institutions.length === 0 && !noSearchResults ? (
              <p className={cn('text-fin-gray-500 py-3 text-sm', isAside ? 'px-2' : 'px-4')}>
                {t('accounts.add.palette.searchHint')}
              </p>
            ) : null}
            {institutions.length > 0 ? (
              // Plaid has no institution pre-select on /link/token/create, so the
              // bank still has to be chosen inside Link itself. Say so up front.
              <p className={cn('text-fin-gray-500 py-2 text-xs', isAside ? 'px-2' : 'px-4')}>
                {t('accounts.add.palette.linkOpensHint')}
              </p>
            ) : null}
          </CommandGroup>
        ) : null}

        {showManualFallback ? (
          <CommandGroup heading={t('accounts.add.palette.enterHoldings')}>
            {(category ? [category] : categories).map((entry) => (
              <PaletteRow
                key={`manual-${entry.id}`}
                compact={isAside}
                title={t(`accounts.add.categories.${entry.id}.manualTitle`)}
                subtitle={t(`accounts.add.categories.${entry.id}.manualSubtitle`)}
                icon={
                  <span
                    className={cn(
                      'border-fin-gray-800 text-fin-gray-500 flex items-center justify-center rounded-xl border',
                      isAside ? 'h-9 w-9' : 'h-10 w-10'
                    )}
                  >
                    <PencilLine className='h-4 w-4' aria-hidden />
                  </span>
                }
                onSelect={() => onSelect({ kind: 'manual', category: entry })}
              />
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </Command>
  );
}

function BreadcrumbChip({ label, onClick }: { label: string; onClick?: () => void }) {
  const className = 'bg-fin-gray-800/70 text-fin-gray-300 rounded-md px-2 py-1 text-xs';
  if (!onClick) return <span className={className}>{label}</span>;
  return (
    <button type='button' onClick={onClick} className={cn(className, 'hover:text-fin-white')}>
      {label}
    </button>
  );
}

function InstitutionAvatar({
  institution,
  compact = false,
}: {
  institution: PlaidInstitution;
  compact?: boolean;
}) {
  const size = compact ? 'h-9 w-9' : 'h-10 w-10';
  if (institution.logo) {
    return (
      <img
        src={`data:image/png;base64,${institution.logo}`}
        alt=''
        className={cn(size, 'rounded-xl object-contain')}
      />
    );
  }
  return (
    <span
      className={cn(
        'bg-fin-accent/15 text-fin-accent flex items-center justify-center rounded-xl',
        size
      )}
    >
      <Building2 className={compact ? 'h-4 w-4' : 'h-5 w-5'} aria-hidden />
    </span>
  );
}

function PaletteRow({
  title,
  subtitle,
  icon,
  trailing,
  disabled,
  compact,
  onSelect,
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  trailing?: React.ReactNode;
  disabled?: boolean;
  compact?: boolean;
  onSelect: () => void;
}) {
  const { t } = useLanguage();
  return (
    <CommandItem
      value={title}
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'data-[selected=true]:bg-fin-gray-800/60 group cursor-pointer gap-3 rounded-lg',
        compact ? 'px-2 py-2' : 'px-3 py-2.5'
      )}
    >
      {icon}
      <div className='min-w-0 flex-1'>
        <p className='text-fin-white truncate text-sm font-medium'>{title}</p>
        {subtitle ? (
          <p className={cn('text-fin-gray-500 text-xs', compact ? 'line-clamp-2' : 'truncate')}>
            {subtitle}
          </p>
        ) : null}
      </div>
      {trailing}
      {!compact ? (
        <span className='text-fin-gray-500 hidden text-xs group-data-[selected=true]:inline'>
          {t('accounts.add.palette.enterHint')}
        </span>
      ) : null}
    </CommandItem>
  );
}
